/**
 * Helpers for article/summary HTML so short text is not shown twice.
 */

export function stripHtmlText(html = "") {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

export function textsOverlap(a, b) {
  if (!a || !b) return false
  if (a === b) return true
  const minLen = Math.min(a.length, b.length, 80)
  if (minLen < 24) return a === b
  return a.slice(0, minLen) === b.slice(0, minLen) || a.includes(b) || b.includes(a)
}

/** Keep only the first <p> (or plain text) for hero/short teaser. */
export function getLeadParagraphHtml(html = "") {
  const source = String(html || "").trim()
  if (!source) return ""

  const match = source.match(/<p\b[^>]*>[\s\S]*?<\/p>/i)
  if (match) return match[0]

  const blockMatch = source.match(/^[\s\S]*?(?=<(?:p|div|h[1-6]|ul|ol)\b|$)/i)
  return (blockMatch?.[0] || source).trim()
}

/** Homepage hero: first important sentence/paragraph only, trimmed length. */
export function getHeroSubtitleHtml(html = "", maxChars = 160) {
  const lead = getLeadParagraphHtml(html)
  if (!lead) return ""

  const text = String(lead)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

  if (!text) return ""

  if (text.length <= maxChars) {
    return `<p>${text}</p>`
  }

  let cut = text.slice(0, maxChars)
  const lastSpace = cut.lastIndexOf(" ")
  if (lastSpace > 80) cut = cut.slice(0, lastSpace)

  return `<p>${cut.trim()}…</p>`
}


/** Drop leading body paragraphs that repeat the short summary. */
export function removeSummaryDuplicateFromBody(html = "", summary = "") {
  const source = String(html || "").trim()
  const summaryText = stripHtmlText(summary)
  if (!source || !summaryText) return source

  let remaining = source
  const paragraphRegex = /^(\s*<p\b[^>]*>[\s\S]*?<\/p>)/i

  for (let i = 0; i < 2; i += 1) {
    const match = remaining.match(paragraphRegex)
    if (!match) break
    const paragraphText = stripHtmlText(match[1])
    if (!textsOverlap(paragraphText, summaryText)) break
    remaining = remaining.slice(match[1].length).trim()
  }

  return remaining
}

/** Remove consecutive duplicate <p> blocks inside HTML. */
export function dedupeConsecutiveParagraphs(html = "") {
  const source = String(html || "").trim()
  if (!source) return ""

  const parts = []
  const regex = /<p\b[^>]*>[\s\S]*?<\/p>/gi
  let lastIndex = 0
  let match
  let lastText = ""

  while ((match = regex.exec(source)) !== null) {
    if (match.index > lastIndex) {
      parts.push(source.slice(lastIndex, match.index))
    }
    const text = stripHtmlText(match[0])
    if (!text || textsOverlap(text, lastText)) {
      lastIndex = match.index + match[0].length
      continue
    }
    parts.push(match[0])
    lastText = text
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < source.length) {
    parts.push(source.slice(lastIndex))
  }

  return parts.join("").trim() || source
}

const STASH_PLACEHOLDER = (index) => `<!--NEWS_STASH_${index}-->`

function stashHtmlBlock(blocks, match) {
  const token = STASH_PLACEHOLDER(blocks.length)
  blocks.push(match)
  return token
}

/** Keep quotes, lists, and other block HTML intact while paragraph helpers run. */
export function withProtectedBlockquotes(html, transform) {
  const source = String(html || "")
  const stashed = []
  // Protect styled cards first so their inner <blockquote> is not extracted.
  let protectedHtml = source.replace(
    /<figure\b[^>]*\bnews-quote\b[^>]*>[\s\S]*?<\/figure>/gi,
    (match) => stashHtmlBlock(stashed, match)
  )
  protectedHtml = protectedHtml.replace(/<blockquote\b[\s\S]*?<\/blockquote>/gi, (match) => {
    return stashHtmlBlock(stashed, match)
  })
  protectedHtml = protectedHtml.replace(/<ul\b[\s\S]*?<\/ul>/gi, (match) => {
    return stashHtmlBlock(stashed, match)
  })
  protectedHtml = protectedHtml.replace(/<ol\b[\s\S]*?<\/ol>/gi, (match) => {
    return stashHtmlBlock(stashed, match)
  })
  const next = transform(protectedHtml)
  return String(next || "").replace(/<!--NEWS_STASH_(\d+)-->/g, (_, index) => {
    return stashed[Number(index)] || ""
  })
}

export function splitArticleHtmlAfterParagraphs(html = "", paragraphCount = 2) {
  const source = String(html || "").trim()
  if (!source || paragraphCount < 1) {
    return { firstPart: source, restPart: "" }
  }

  const paragraphRegex = /<p\b[^>]*>[\s\S]*?<\/p>/gi
  let match
  let count = 0
  let splitAt = -1

  while ((match = paragraphRegex.exec(source)) !== null) {
    count += 1
    if (count === paragraphCount) {
      splitAt = match.index + match[0].length
      break
    }
  }

  if (splitAt === -1) {
    return { firstPart: source, restPart: "" }
  }

  return {
    firstPart: source.slice(0, splitAt).trim(),
    restPart: source.slice(splitAt).trim(),
  }
}
